import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";

//Import Components
import ExplorePartnersSection from "../Dashboard/ExplorePartnersSection";
import RedeemVoucherModal from "../../components/Common/RedeemVoucherModal";

//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";
import fallbackImage from "../../assets/images/user-dashboard/image.svg";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const RestaurantsPage = () => {
  document.title = "Restaurants | Omaha Food Lovers";
  const navigate = useNavigate();

  const handleNavigateToWallet = () => {
    navigate("/wallet");
  };

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRestaurants, setTotalRestaurants] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("rating_desc");
  const [visitedFilter, setVisitedFilter] = useState("all"); // all, visited, not_visited
  const [categories, setCategories] = useState([]);
  const [categoriesCurrentPage, setCategoriesCurrentPage] = useState(1);
  const [categoriesHasMore, setCategoriesHasMore] = useState(true);
  const [categoriesLoadingMore, setCategoriesLoadingMore] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Fallback location for distance calculation when user location is not available
  const fallbackLat = 41.2565;
  const fallbackLng = -95.9345;

  // Redeem modal state
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const [redeemModalRestaurant, setRedeemModalRestaurant] = useState(null);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Calculate distance between two coordinates using Haversine formula (returns miles)
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;

    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance.toFixed(1);
  }, []);

  // Debounce search value (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch categories from API
  const fetchCategories = useCallback(async (page = 1, append = false) => {
    try {
      const response = await get(`${endpoints.categories}?page=${page}`);
      const apiCategories = response.data.data?.data || [];
      const pagination = response.data.data;
      
      // Transform API categories to component format
      const transformedCategories = apiCategories.map((cat) => ({
        id: cat.id.toString(),
        label: cat.title,
        icon: cat.icon || cat.avatar_icon,
      }));

      if (append) {
        // Append new categories to existing list
        setCategories((prev) => [...prev, ...transformedCategories]);
      } else {
        // Set initial categories with "All" at the beginning
        setCategories([
          { id: "all", label: "All", icon: "mdi-view-grid-outline" },
          ...transformedCategories,
        ]);
      }

      // Update pagination state
      setCategoriesCurrentPage(pagination?.current_page || page);
      setCategoriesHasMore(pagination?.current_page < pagination?.last_page);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      // Use default categories as fallback
      if (!append) {
        setCategories([
          { id: "all", label: "All", icon: "mdi-view-grid-outline" },
        ]);
      }
    }
  }, []);

  // Load more categories (pagination)
  const loadMoreCategories = useCallback(async () => {
    if (categoriesLoadingMore || !categoriesHasMore) return;

    setCategoriesLoadingMore(true);
    const nextPage = categoriesCurrentPage + 1;
    await fetchCategories(nextPage, true);
    setCategoriesLoadingMore(false);
  }, [categoriesLoadingMore, categoriesHasMore, categoriesCurrentPage, fetchCategories]);

  // Fetch user points
  const fetchUserPoints = useCallback(async () => {
    try {
      const response = await get(endpoints.pointsBalance);
      const apiPoints = response.data.data?.points || 0;
      setUserPoints(apiPoints);
    } catch (err) {
      console.error("Failed to fetch points:", err);
      setUserPoints(0);
    }
  }, []);

  const transformRestaurantData = useCallback(
    (apiRestaurant) => {
      const categories = apiRestaurant.categories || [];
      const locationCategories = apiRestaurant.location_categories || [];
      const allCategories =
        categories.length > 0 ? categories : locationCategories;
      const firstCategory = allCategories[0];

      const isValidImageUrl = (url) => {
        if (!url) return false;
        if (
          url.includes("encrypted-tbn0.gstatic.com") &&
          url.includes("q=tbn:")
        ) {
          return false;
        }
        return true;
      };

      const bannerImage = isValidImageUrl(apiRestaurant.banner_url)
        ? apiRestaurant.banner_url
        : fallbackImage;

      const restaurantLat =
        apiRestaurant.latitude || apiRestaurant.address?.latitude;
      const restaurantLng =
        apiRestaurant.longitude || apiRestaurant.address?.longitude;
      const locationToUse = userLocation || {
        lat: fallbackLat,
        lng: fallbackLng,
      };
      const distance =
        restaurantLat && restaurantLng
          ? `${calculateDistance(locationToUse.lat, locationToUse.lng, restaurantLat, restaurantLng)} mi`
          : null;

      return {
        id: apiRestaurant.id,
        name: apiRestaurant.title,
        image: bannerImage,
        badge: apiRestaurant.premium
          ? { label: "Premium", variant: "popular" }
          : null,
        rating: apiRestaurant.resturant_rating?.rating || 0,
        cuisine: firstCategory?.title || "Restaurant",
        categories: allCategories.map((cat) => cat.title),
        distance,
        category: firstCategory?.slug || "all",
        liked: apiRestaurant.is_wishlist || false,
        is_visited: apiRestaurant.is_visited || 0,
        latitude: restaurantLat,
        longitude: restaurantLng,
      };
    },
    [userLocation, calculateDistance, fallbackLat, fallbackLng],
  );

  // Fetch restaurant list
  const fetchRestaurants = useCallback(
    async (page = 1, isLoadMore = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        // Build query parameters
        const params = new URLSearchParams({
          is_member: "1",
          page_size: "8",
          page: page.toString(),
        });

        // Add sort_by only if it's not empty
        if (sortBy) {
          params.append("sort_by", sortBy);
        }

        // Add category_id if not "all"
        if (activeCategory !== "all") {
          params.append("category_id", activeCategory);
        }

        // Add search_by if search value exists
        if (debouncedSearchValue.trim()) {
          params.append("search_by", debouncedSearchValue.trim());
        }

        // Add is_visited filter if not "all"
        if (visitedFilter === "visited") {
          params.append("is_visited", "1");
        } else if (visitedFilter === "not_visited") {
          params.append("is_visited", "0");
        }

        const response = await get(
          `${endpoints.restaurantList}?${params.toString()}`,
        );
        const transformedData = response.data.data.map(transformRestaurantData);
        const currentPage = response.data.current_page || 1;
        const lastPage = response.data.last_page || 1;

        if (isLoadMore) {
          setRestaurants((prev) => {
            // Deduplicate restaurants by ID to prevent duplicates
            const existingIds = new Set(prev.map(r => r.id));
            const newRestaurants = transformedData.filter(r => !existingIds.has(r.id));
            
            // Only append if we have new items
            if (newRestaurants.length === 0) {
              return prev;
            }
            
            return [...prev, ...newRestaurants];
          });
        } else {
          setRestaurants(transformedData);
        }

        // Check if there are more pages
        setHasMore(currentPage < lastPage);
        setCurrentPage(currentPage);
        
        // Set total count from API response
        if (response.data.total !== undefined) {
          setTotalRestaurants(response.data.total);
        }
      } catch (err) {
        const errorMessage = extractErrorMessage(err);
        toastr.error(errorMessage);
        // Use empty array as fallback
        if (!isLoadMore) {
          setRestaurants([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      transformRestaurantData,
      activeCategory,
      debouncedSearchValue,
      sortBy,
      visitedFilter,
    ],
  );

  // Viewport detection for large screens
  const scrollContainerRef = React.useRef(null);
  
  useEffect(() => {
    const checkIfFitsInViewport = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight } = scrollContainerRef.current;
        setShowLoadMoreButton(scrollHeight <= clientHeight && hasMore && !loadingMore);
      }
    };

    checkIfFitsInViewport();
    
    const resizeObserver = new ResizeObserver(checkIfFitsInViewport);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [restaurants, hasMore, loadingMore]);

  // Fetch categories once on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch user points on mount
  useEffect(() => {
    fetchUserPoints();
  }, [fetchUserPoints]);

  // Check for stored location permission and request if needed
  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) {
      setUserLocation(JSON.parse(storedLocation));
    } else {
      setShowLocationModal(true);
    }
  }, []);

  const handleAllowLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(location);
          localStorage.setItem("userLocation", JSON.stringify(location));
          setShowLocationModal(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          toastr.error(
            "Unable to get your location. Distance will not be calculated.",
          );
          setShowLocationModal(false);
        },
      );
    } else {
      toastr.error("Geolocation is not supported by your browser.");
      setShowLocationModal(false);
    }
  };

  const handleDenyLocation = () => {
    setShowLocationModal(false);
  };

  const handleToggleLike = (restaurantId, isLiked) => {
    setRestaurants((prev) =>
      prev.map((r) => (r.id === restaurantId ? { ...r, liked: isLiked } : r)),
    );
  };

  const handleRatingChanged = () => {
    // Reload restaurants to get updated average ratings
    fetchRestaurants(1);
  };

  // Fetch restaurants when filters change
  useEffect(() => {
    fetchRestaurants(1, false);
  }, [fetchRestaurants]);

  // Handle scroll-based pagination
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchRestaurants(currentPage + 1, true);
    }
  };

  // Filter change handlers
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setHasMore(true);
  };

  const handleSortChange = (sortValue) => {
    setSortBy(sortValue);
    setCurrentPage(1);
    setHasMore(true);
  };

  const handleVisitedFilterChange = (filterValue) => {
    console.log("handleVisitedFilterChange called with:", filterValue);
    setVisitedFilter(filterValue);
    setCurrentPage(1);
    setHasMore(true);
  };

  // Redeem handlers
  const handleRedeemRestaurant = (restaurant) => {
    setRedeemModalRestaurant({
      id: restaurant.id,
      name: restaurant.name,
      image: restaurant.image,
      category: restaurant.cuisine || restaurant.category,
      location: restaurant.distance || "Nearby",
    });
    setIsRedeemModalOpen(true);
  };

  const handleCloseRedeemModal = () => {
    setIsRedeemModalOpen(false);
    setRedeemModalRestaurant(null);
  };

  const handleVoucherCreated = () => {
    // Refresh user points after redemption
    fetchUserPoints();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <ExplorePartnersSection
            restaurants={restaurants}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            totalRestaurants={totalRestaurants}
            onLoadMore={handleLoadMore}
            onSelectRestaurant={(id) => console.log("open restaurant", id)}
            onRedeemRestaurant={handleRedeemRestaurant}
            onToggleLike={handleToggleLike}
            onRatingChanged={handleRatingChanged}
            onOpenFilters={() => console.log("open filters panel")}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            categories={categories}
            categoriesHasMore={categoriesHasMore}
            categoriesLoadingMore={categoriesLoadingMore}
            onLoadMoreCategories={loadMoreCategories}
            visitedFilter={visitedFilter}
            onVisitedFilterChange={handleVisitedFilterChange}
          />
          {/* Load More button for large screens where content fits in viewport */}
          {showLoadMoreButton && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <button
                className="btn btn-primary"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading..." : "Load More Restaurants"}
              </button>
            </div>
          )}
        </Container>
      </div>

      {/* Location Permission Modal */}
      {showLocationModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            <h3
              style={{
                marginBottom: "12px",
                fontSize: "20px",
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Enable Location
            </h3>
            <p
              style={{
                marginBottom: "24px",
                fontSize: "14px",
                color: "#666",
                lineHeight: "1.5",
              }}
            >
              Allow us to access your location to show restaurants near you and
              calculate distances.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleDenyLocation}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  color: "#666",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Deny
              </button>
              <button
                onClick={handleAllowLocation}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#ec421c",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Allow
              </button>
            </div>
          </div>
        </div>
      )}

      <RedeemVoucherModal
        isOpen={isRedeemModalOpen}
        onClose={handleCloseRedeemModal}
        userPoints={userPoints}
        onVoucherCreated={handleVoucherCreated}
        onNavigateToWallet={handleNavigateToWallet}
        preSelectedRestaurant={redeemModalRestaurant}
      />
    </React.Fragment>
  );
};

export default RestaurantsPage;
